import dayjs from "dayjs"
import { dayJstoDateOnly } from "../../../../utils/dayjs-to-date-only"
import { CalendarGrid } from "./grid"
import { CalendarTable } from "./table"
import {
  CalendarAppointment,
  eAppointmentCountingMethod,
  eAppointmentCountingMethod_AllValues,
} from "../../../../@client/Theasoft_ApiServer_Calendar"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { useIsSmallScreen } from "@/utils/useIsSmallScreen"

export interface RenderCalendarProps {
  startDate: Date
  endDate: Date
  eventsByDate: Map<string, CalendarAppointment[]>
  statusByDate: Map<string, CalendarAppointment[]>
  holidaysByDate: Set<string>
  schoolHolidaysByDate: Set<string>
  showEndTime?: boolean
}

interface Props {
  view: "grid" | "table"
  startDate?: Date
  endDate?: Date
  countingMethod?: eAppointmentCountingMethod
  showEndTime?: boolean
}

export function Calendar({
  view,
  startDate,
  endDate,
  countingMethod,
  showEndTime,
}: Props) {
  const isSmallScreen = useIsSmallScreen()
  const defaultStart = isSmallScreen
    ? dayjs().startOf("month")
    : dayjs().startOf("year")
  const defaultEnd = isSmallScreen
    ? dayjs().endOf("month").add(2, "month")
    : dayjs().endOf("year")
  const [start, setStart] = React.useState(defaultStart)
  const [end, setEnd] = React.useState(defaultEnd)
  React.useEffect(() => {
    if (startDate && endDate) {
      setStart(dayjs(startDate))
      setEnd(dayjs(endDate))
      return
    }

    if (isSmallScreen) {
      setStart(dayjs().startOf("month"))
      setEnd(dayjs().endOf("month").add(2, "month"))
    } else {
      setStart(dayjs().startOf("year"))
      setEnd(dayjs().endOf("year"))
    }
  }, [isSmallScreen, startDate, endDate])
  const dataStart = start.startOf("isoWeek")
  const dataEnd = end.endOf("isoWeek")

  const { data: holidays } = useSuspenseQueryGet(
    "/api/Holiday/GetHolidaysForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
    }
  )

  const { data: schoolHolidays } = useSuspenseQueryGet(
    "/api/SchoolHoliday/GetSchoolHolidaysForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
    }
  )

  const { data: allEvents } = useSuspenseQueryGet(
    "/api/Calendar/GetMyEventsForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
      method: countingMethod ?? eAppointmentCountingMethod_AllValues[1],
      withPossibleCasts: true,
      onlyReleasedDuties: false,
      withPersStatus: false,
    }
  )

  const { data: allStatus } = useSuspenseQueryGet(
    "/api/Calendar/GetMyStatusForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
    }
  )

  const eventsByDate = new Map(
    allEvents.map(([date, events]) => [
      dayjs(date).format("YYYY-MM-DD"),
      events,
    ])
  )

  const statusByDate = new Map(
    allStatus.map(([date, events]) => [
      dayjs(date).format("YYYY-MM-DD"),
      events,
    ])
  )

  const holidaysByDate = new Set(
    holidays.map((holiday) => dayjs(holiday.date).format("YYYY-MM-DD"))
  )

  const schoolHolidaysByDate = schoolHolidays.reduce((set, holiday) => {
    const start = dayjs(holiday.startDate)
    const end = dayjs(holiday.endDate)

    for (
      let d = start;
      d.isBefore(end) || d.isSame(end, "day");
      d = d.add(1, "day")
    ) {
      set.add(d.format("YYYY-MM-DD"))
    }

    return set
  }, new Set<string>())

  const calendarComponent =
    view === "grid" ? (
      <CalendarGrid
        endDate={end.toDate()}
        startDate={start.toDate()}
        eventsByDate={eventsByDate}
        holidaysByDate={holidaysByDate}
        schoolHolidaysByDate={schoolHolidaysByDate}
        statusByDate={statusByDate}
      />
    ) : (
      <CalendarTable
        endDate={end.toDate()}
        startDate={start.toDate()}
        eventsByDate={eventsByDate}
        holidaysByDate={holidaysByDate}
        schoolHolidaysByDate={schoolHolidaysByDate}
        statusByDate={statusByDate}
        showEndTime={showEndTime}
      />
    )

  return calendarComponent
}
